import {type Components, type Theme} from "@mui/material";
import DialogBackdropNoCloseOnClick from "./DialogBackdropNoCloseOnClick";

export const components: Components<Omit<Theme, "components">> = {
    MuiInputBase: {
        defaultProps: {
            // #48085/#55889: intentionally an invalid token — Chrome ignores
            // "off" for its address-save heuristic but skips fields with
            // unknown tokens. Lives on InputBase so it also covers inputs
            // not rendered through MuiTextField.
            autoComplete: "false",
        },
        styleOverrides: {
            root: {
                // var(--an-surface) flips between #FFFFFF (light) and #1A2236
                // (dark) so inputs stay visually distinct from the page bg.
                backgroundColor: "var(--an-surface)",
            },
            input: {
                "&::placeholder": {
                    color: "var(--an-muted-4)",
                    opacity: 1,
                    fontStyle: "italic",
                },
                "&:-ms-input-placeholder": {
                    color: "var(--an-muted-4)",
                    fontStyle: "italic",
                },
                "&::-ms-input-placeholder": {
                    color: "var(--an-muted-4)",
                    fontStyle: "italic",
                },
            },
        }
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                borderRadius: "4px",
                "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--an-border-2)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#48BD9C",
                    borderWidth: "1px",
                },
            }
        }
    },
    MuiCheckbox: {
        styleOverrides: {
            root: {
                color: "var(--an-ink)",
                "&.Mui-checked": {
                    color: "#48BD9C",
                },
            }
        }
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: {
                // #55074: the default MUI tooltip (11px) was too small to read.
                // Dark bg + white text is mode-independent so it stays legible in
                // both light and dark themes.
                fontFamily: "'Lato', system-ui, sans-serif",
                fontSize: "13px",
                lineHeight: "18px",
                padding: "8px 12px",
                maxWidth: 360,
                backgroundColor: "#181D27",
                color: "#fff",
            },
            arrow: {
                color: "#181D27",
            },
        }
    },
    MuiTextField: {
        defaultProps: {
            autoComplete: "none",
            // disable autocomplete for 1password fields
            slotProps: {
                htmlInput: {
                    "data-op-ignore": "true",
                }
            }
        },
    },
    MuiAutocomplete: {
        styleOverrides: {
            root: {
                backgroundColor: "var(--an-surface)",
            },
        }
    },
    MuiFormControl: {
        styleOverrides: {
            root: {
                width: "100%",
            },
        }
    },
    MuiMenu: {
        defaultProps: {
            disableScrollLock: true,
        },
        styleOverrides: {
            paper: {
                width: "320px",
                minWidth: "100%",
                maxHeight: "300px",
                overflowY: "auto",
                borderRadius: "0px",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.15)",
                marginTop: "2px",
            },
        }
    },
    MuiList: {
        styleOverrides: {
            root: {
                paddingTop: 0,
                paddingBottom: 0,
            }
        }
    },
    MuiListItem: {
        styleOverrides: {
            root: {
                fontFamily: "sofo-400, Courier, Arial, sans-serif",
                fontSize: "0.875rem",
                backgroundColor: "var(--an-surface)",
                "&:hover": {
                    backgroundColor: "var(--an-panel)",
                },
                "&.Mui-selected": {
                    backgroundColor: "var(--an-border-2)",
                },
                "&.Mui-selected:hover": {
                    backgroundColor: "var(--an-panel)",
                },
            }
        }
    },
    MuiInputAdornment: {
        styleOverrides: {
            root: {
                color: "rgba(213,214,214,1.00)",

            },
        },
    },
    MuiButton: {
        styleOverrides: {
            root: {
                "&.MuiButtonBase-root.MuiButton-root": {
                    // No `color` here — each variant rule below sets its own
                    // color. Setting it at this 3-class-specificity selector
                    // would override the 2-class variant rules (yellow Save,
                    // mint Contained etc.) and break readability in dark mode.
                    fontFamily: "'Lato', system-ui, Arial, sans-serif",
                    fontWeight: 700,
                    fontSize: "16px",
                    lineHeight: 1,
                    letterSpacing: "0",
                    textTransform: "none",
                    borderRadius: "4px",
                    boxShadow: "none",
                    transition: "background-color 150ms ease-out, border-color 150ms ease-out, color 150ms ease-out",
                },
                "&.MuiButton-text": {
                    color: "var(--an-ink)",
                },
                // Brand-green contained button — ink text reads on the mint
                // surface in both modes, so the literal #0E1529 stays.
                "&.MuiButton-contained": {
                    backgroundColor: "#48BD9C",
                    color: "#0E1529",
                    paddingLeft: "24px",
                    paddingRight: "24px",
                },
                "&.MuiButton-contained:not(.MuiButton-sizeSmall)": {
                    minHeight: "56px",
                },
                "&.MuiButton-contained:hover": {
                    backgroundColor: "#3CB28F",
                    boxShadow: "none",
                },
                "&.MuiButton-contained:active": {
                    backgroundColor: "#2EA37E",
                },
                "&.MuiButton-contained.Mui-disabled": {
                    backgroundColor: "var(--an-border)",
                    color: "var(--an-muted-4)",
                },
                "&.MuiButton-outlined": {
                    backgroundColor: "transparent",
                    color: "var(--an-ink)",
                    border: "2px solid var(--an-ink)",
                    paddingLeft: "24px",
                    paddingRight: "24px",
                },
                "&.MuiButton-outlined:not(.MuiButton-sizeSmall)": {
                    minHeight: "56px",
                },
                "&.MuiButton-outlined:hover": {
                    backgroundColor: "var(--an-panel)",
                    borderColor: "var(--an-ink)",
                    color: "var(--an-ink)",
                },
                "&.MuiButton-outlined.remove-button": {
                    color: "var(--an-rose-text)",
                    borderColor: "var(--an-rose-text)",
                    "& .MuiButton-startIcon, & .MuiButton-endIcon": {color: "var(--an-rose-text)"},
                },
                "&.MuiButton-outlined.remove-button:hover": {
                    color: "#fff",
                    backgroundColor: "#BD4769",
                    borderColor: "#BD4769",
                },
                "&.bg-save-button, &.bg-add-button": {
                    backgroundColor: "#FDCD43",
                    color: "#0E1529",
                    minHeight: "48px",
                    border: "none !important",
                    paddingLeft: "24px",
                    paddingRight: "24px",
                },
                "&.bg-save-button:hover, &.bg-add-button:hover": {
                    backgroundColor: "#EEC03A",
                },
                // Disabled state for yellow Save/Add — MUI's default
                // disabled muting on a saturated yellow looks dirty in dark
                // mode. Pin to neutral border/muted colors instead.
                "&.bg-save-button.Mui-disabled, &.bg-add-button.Mui-disabled": {
                    backgroundColor: "var(--an-border)",
                    color: "var(--an-muted-4)",
                },
                "&.bg-an-yellow": {
                    backgroundColor: "#FDCD43",
                    color: "#0E1529",
                    minHeight: "48px",
                    border: "none !important",
                },
                "&.bg-an-yellow:hover": {
                    backgroundColor: "#EEC03A",
                },
            },
        },
    },
    MuiTableCell: {
        styleOverrides: {
            root: {
                fontFamily: "sofo-400, Courier, Arial, sans-serif",
                fontWeight: "400",
                fontSize: "calc(10px + (18 - 10) * (100vw - 1024px) / (3840 - 1024))",
            }
        }
    },
    MuiSvgIcon: {
        styleOverrides: {
            root: {
                fontSize: "1.75em"
            }
        }
    },
    // The enlarged icon above is taller than a text line, so the MUI default (top
    // alignment) left one-line alerts sitting above the icon's center.
    MuiAlert: {
        styleOverrides: {
            root: {
                alignItems: "center",
            },
            action: {
                alignItems: "center",
                paddingTop: 0,
            },
        },
    },
    MuiDialog: {
        defaultProps: {
            container: document.getElementById("root"),
            slots: {
                backdrop: DialogBackdropNoCloseOnClick,
            },
        },
        styleOverrides: {
            paper: {
                borderRadius: "20px",
                backgroundColor: "var(--an-surface)",
                boxShadow: "0 8px 8px -4px rgba(10, 13, 18, 0.04), 0 20px 24px -4px rgba(10, 13, 18, 0.10)",
                fontFamily: "'Lato', system-ui, sans-serif",
            },
        },
    },
    MuiDialogTitle: {
        styleOverrides: {
            root: {
                fontFamily: "'Lato', system-ui, sans-serif",
                fontWeight: 700,
                color: "var(--an-heading)",
                fontSize: "20px",
                lineHeight: "28px",
                padding: "24px 32px 16px",
            },
        },
    },
    MuiDialogContent: {
        styleOverrides: {
            root: {
                padding: "16px 32px",
            },
        },
    },
    MuiDialogActions: {
        styleOverrides: {
            root: {
                padding: "16px 32px 24px",
                gap: "12px",
            },
        },
    },
    MuiPopover: {
        defaultProps: {
            container: document.getElementById("root"),
        },
    },
    MuiDivider: {
        styleOverrides: {
            root: {
                borderColor: "var(--an-border-2)",
            },
        },
    },
    // MUI's default chip leans on theme.palette.action which renders as a
    // near-transparent grey on dark surfaces — basically invisible.
    // var(--an-border-2) is the only token that sits LIGHTER than the
    // surface in dark mode and DARKER than the surface in light mode,
    // so the chip reads as a tag in both. Text uses var(--an-ink) which
    // already inverts (dark ink on light bg / light ink on dark bg).
    MuiChip: {
        styleOverrides: {
            root: {
                backgroundColor: "var(--an-border-2)",
                color: "var(--an-ink)",
                "& .MuiChip-deleteIcon": {
                    color: "var(--an-ink)",
                    opacity: 0.7,
                },
                "& .MuiChip-deleteIcon:hover": {
                    color: "var(--an-ink)",
                    opacity: 1,
                },
            },
        },
        // #56518: `variant="tag"` marks a chip that represents an active
        // selection (applied filter, chosen mail-design state) as opposed to
        // the neutral grey default. Fill and hairline change; text and delete
        // icon inherit the ink from the root override, so the variant works
        // in both color schemes via --an-tag-bg / --an-tag-border.
        // The hairline is an inset shadow rather than a border on purpose:
        // the pale fill needs an edge on grey panels, but a border would
        // eat into the 32px box and shift the label padding like MUI's
        // "outlined" variant does. The shadow leaves the geometry untouched.
        // A clickable tag (a filter chip that is on) keeps fill and hairline on
        // hover, focus and press; MUI's clickable chip would darken it and hide
        // the state (#56642).
        variants: [
            {
                props: {variant: "tag"},
                style: {
                    backgroundColor: "var(--an-tag-bg)",
                    boxShadow: "inset 0 0 0 1px var(--an-tag-border)",
                    "&:hover, &.Mui-focusVisible, &:active": {
                        backgroundColor: "var(--an-tag-bg)",
                        boxShadow: "inset 0 0 0 1px var(--an-tag-border)",
                    },
                },
            },
        ],
    },
    // #56642: segmented choices in the house look instead of MUI's uppercase grey;
    // the selected segment takes the "active selection" tint like a tag chip.
    MuiToggleButton: {
        styleOverrides: {
            root: {
                textTransform: "none",
                fontFamily: "'Lato', system-ui, Arial, sans-serif",
                fontSize: "16px",
                color: "var(--an-ink)",
                borderColor: "var(--an-border-2)",
                backgroundColor: "var(--an-surface)",
                "&.Mui-selected, &.Mui-selected:hover": {
                    backgroundColor: "var(--an-tag-bg)",
                    color: "var(--an-ink)",
                    fontWeight: 700,
                },
            },
        },
    },
};
